import { ImageListItem, ImageList as MuiImageList } from '@material-ui/core';
import { Theme } from '@emotion/react';
import { imageListContainer, imageListOnlyOne } from 'omakase/ImageList/imageListCssDefs';
import React from 'react';
import _ from 'lodash';
import useResizeObserver from '@react-hook/resize-observer';

export interface ImageListImage {
  /**
   * Alt text for the image
   */
  alt: string;

  /**
   * src for the image; mobile scale is assumed
   */
  src: string;

  /**
   * srcSet for the image;
   */
  srcSet?: string;

  /**
   * the image id
   */
  id?: number;
}

export type onClickImageArgs = { alt: string; index: number; src: string; id?: number };

export interface ImageListProps {
  /**
   * Images to be displayed.
   */
  images: ImageListImage[];
  /**
   * Handler to be called on clicking a given image. Arguments will indicate the
   * src, alt, and index of the clicked image.
   */
  onClick(args: onClickImageArgs): void;
  /**
   * A fixed width for the ImageList, in pixels. If it is unspecified, the ImageList
   * will fill 100% of the width of its container.
   *
   * Note that if this is left unspecified, an in-component ResizeObserver will ensure that
   * any changes to the div get reflected down into the computed rowHeight to use.
   */
  fixedWidth?: number;
}

/* We lay the images out, alternating between laying out a "double-sized" image and two side-by-side
 * "single-sized" images.
 * Which one we start with depends on how many images we have - if num_images mod 3 is 2, we should
 * start with half-sized, otherwise we start with full. The only exception here is if we have exactly
 * 2 images, in which case both are laid out as "double-sized".
 */
function computeDimension(index: number, size: number): 1 | 2 {
  if (size === 2) {
    return 2;
  }
  // We return 2 for a "double-indicating" value of index % 3, 1 otherwise. If size % 3 is 2,
  // images 2, 5, 8, ... are double-sided; otherwise images 0, 3, 6, ... are.
  const doubleIndicating = size % 3 === 2 ? 2 : 0;
  return index % 3 === doubleIndicating ? 2 : 1;
}

const ImageList: React.FunctionComponent<ImageListProps> = ({
  images,
  images: { length },
  onClick,
  fixedWidth,
}: ImageListProps) => {
  const [measuredWidth, setMeasuredWidth] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  // Call setMeasuredWidth appropriately for the initial width of the containerDiv
  React.useLayoutEffect(() => {
    if (containerRef.current) {
      setMeasuredWidth(containerRef.current.getBoundingClientRect().width);
    }
  }, [containerRef]);

  // Track any changes to its size as well
  useResizeObserver(
    containerRef,
    _.throttle(({ contentRect: { width } }: ResizeObserverEntry) => setMeasuredWidth(width), 20, {
      leading: true,
      trailing: true,
    })
  );
  // The useResizeObserver hook will also automatically unregister on unmount.

  if (length === 0) {
    return null;
  }

  if (length === 1) {
    const { alt, src, srcSet, id } = images[0];
    return (
      <button
        css={(theme: Theme) => imageListOnlyOne(theme, fixedWidth)}
        onClick={() => onClick({ alt, src, index: 0, id })}
      >
        <img src={src} alt={alt} srcSet={srcSet} />
      </button>
    );
  }

  const imageListItems = images.map(({ alt, src, srcSet, id }, index) => {
    const dimension = computeDimension(index, length);
    return (
      <ImageListItem cols={dimension} rows={dimension} key={index}>
        <button
          data-testid={`image-${index}`}
          aria-label={`View image of ${alt} (opens a Dialog)`}
          onClick={() => onClick({ alt, src, index, id })}
        >
          <img src={src} alt={alt} srcSet={srcSet} />
        </button>
      </ImageListItem>
    );
  });

  return (
    <div css={(theme: Theme) => imageListContainer(theme, fixedWidth)} ref={containerRef}>
      <MuiImageList
        /* In current MUI, we will need to specify the variant as follows, but that doesn't exist
         * in the version that we're on, 4.1.2. For that version we just need to consider our
         * ImageList usage as being "advanced", and the desired behavior is inferred. */
        // variant="quilted"
        cols={2}
        gap={2}
        /* We want the images to be in a 3:2 ratio. But a "row" displays two images across its width,
         * left-to-right. The inverse of the desired width:height ratio is 2/3, but this is for the
         * two-row, two-column spanning "large" images. The small ones, which occupy a single row's
         * worth of height, should be given half that space. Therefore, rowHeight should simply be
         * (component width / 3). */
        rowHeight={(fixedWidth ?? measuredWidth) / 3}
      >
        {imageListItems}
      </MuiImageList>
    </div>
  );
};

export default ImageList;
